import React from 'react'
import {SingleTeaserInterface} from '../../../types/teaser'
import {ArrowRight} from '@volkswagen-onehub/icons-core'
import {
    Breakpoints,
    Layout,
    LayoutRowGap,
    Text,
    Image,
    styled,
    TextTag,
    CTA,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import {boxedContainer, gridUtils} from '../../../assets/css-vars/gridUtils'
import setTracking, {
    checkTrackingManager,
} from '../../../utils/tracking/tracking-utils/setTracking'
import TrackingAction from '../../../tracking-manager'
import {useFeatureServices} from '../../../use-feature-services'

const TeaserContainer = styled.div`
    ${boxedContainer}
    padding-right: ${gridUtils.gap};
    padding-left: ${gridUtils.gap};

    &:nth-child(even) {
        padding-top: ${gridUtils.gapDouble};
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        padding-right: 0;
        padding-left: 0;
    }
`
const Title = styled(Text)`
    line-height: 1.2;
`
const EditorialContainer = styled.div`
    display: grid;
    gap: 8px;

    @media (min-width: ${Breakpoints.b960}px) {
        gap: 14px;
    }
`

// Not a link on purpose: the CTA has the same destination and is the only link of
// the teaser (no redundant links); the image click is just a bigger target for
// mouse users
const MediaContainer = styled.div`
    cursor: pointer;
    width: 100%;
    height: 0px;
    position: relative;
    padding-bottom: 56.25%;

    @media (min-width: ${Breakpoints.b960}px) {
        padding-bottom: 0;
        height: auto;
    }
`

const ImageMedia = styled(Image)`
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 16px;

    @media (min-width: ${Breakpoints.b960}px) {
        position: relative;
        height: auto;
    }
`

interface TeaserProps {
    teaser: SingleTeaserInterface
    index: number
}

export default function Teaser(props: TeaserProps): React.JSX.Element {
    const {tracking: trackingManager} = useFeatureServices()

    const trackingClickOnTeaser = () => {
        if (checkTrackingManager(trackingManager)) {
            setTracking(trackingManager, TrackingAction.VWBasic_Button_Click, {
                eventInfo: {
                    contentId: props.teaser.title,
                },
                link: {
                    name: props.teaser.cta_text,
                    url: props.teaser.cta_url,
                },
            })
        }
    }
    const trackingClickOnImageTeaser = () => {
        if (checkTrackingManager(trackingManager)) {
            setTracking(trackingManager, TrackingAction.VWBasic_Teaser_Click, {
                eventInfo: {
                    contentId: props.teaser.title,
                },
                link: {
                    name: props.teaser.cta_text,
                    url: props.teaser.cta_url,
                },
            })
        }
    }
    const openTeaserFromImage = (e: React.MouseEvent) => {
        trackingClickOnImageTeaser()

        if (props.teaser.cta_target === '_blank' || e.ctrlKey || e.metaKey) {
            window.open(props.teaser.cta_url, '_blank', 'noopener')

            return
        }

        window.location.assign(props.teaser.cta_url)
    }

    return (
        <>
            <TeaserContainer>
                <Layout
                    appearance={{
                        [Breakpoints.default]: [
                            {
                                name: 'a',
                                columns: 24,
                            },
                            {
                                name: 'b',
                                columns: 24,
                            },
                        ],
                        [Breakpoints.b560]:
                            props.index % 2 === 0
                                ? [
                                      {
                                          name: 'a',
                                          columns: 12,
                                      },
                                      {
                                          name: '.',
                                          columns: 2,
                                      },
                                      {
                                          name: 'b',
                                          columns: 10,
                                      },
                                  ]
                                : [
                                      {
                                          name: '.',
                                          columns: 1,
                                      },
                                      {
                                          name: 'b',
                                          columns: 8,
                                      },
                                      {
                                          name: '.',
                                          columns: 2,
                                      },
                                      {
                                          name: 'a',
                                          columns: 12,
                                      },
                                  ],
                        [Breakpoints.b960]:
                            props.index % 2 === 0
                                ? [
                                      {
                                          name: 'a',
                                          columns: 11,
                                      },
                                      {
                                          name: '.',
                                          columns: 3,
                                      },
                                      {
                                          name: 'b',
                                          columns: 8,
                                      },
                                      {
                                          name: '.',
                                          columns: 2,
                                      },
                                  ]
                                : [
                                      {
                                          name: '.',
                                          columns: 2,
                                      },
                                      {
                                          name: 'b',
                                          columns: 8,
                                      },
                                      {
                                          name: '.',
                                          columns: 3,
                                      },
                                      {
                                          name: 'a',
                                          columns: 11,
                                      },
                                  ],
                    }}
                    rowGap={LayoutRowGap.static350}
                >
                    <MediaContainer onClick={openTeaserFromImage}>
                        <ImageMedia
                            src={props.teaser.image}
                            draggable={false}
                            alt={
                                props.teaser.title
                                    ? props.teaser.title
                                    : props.teaser.headline
                            }
                        />
                    </MediaContainer>

                    <EditorialContainer>
                        {props.teaser.title ? (
                            <Title
                                tag={TextTag.h2}
                                appearance={TokenTextAppearance.copy200}
                            >
                                <b>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: props.teaser.title,
                                        }}
                                    />
                                </b>
                            </Title>
                        ) : undefined}
                        {props.teaser.headline ? (
                            <Text
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.copy200}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: props.teaser.headline,
                                    }}
                                />
                            </Text>
                        ) : undefined}

                        <div>
                            <CTA
                                tag="a"
                                href={props.teaser.cta_url}
                                target={
                                    props.teaser.cta_target === '_blank'
                                        ? '_blank'
                                        : '_self'
                                }
                                onClick={trackingClickOnTeaser}
                                icon={
                                    <ArrowRight
                                        variant="small"
                                        aria-hidden={true}
                                    />
                                }
                                iconPosition="end"
                                emphasis="secondary"
                                size="small"
                                title={props.teaser.cta_text}
                                aria-label={props.teaser.cta_text}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: props.teaser.cta_text,
                                    }}
                                />
                            </CTA>
                        </div>
                    </EditorialContainer>
                </Layout>
            </TeaserContainer>
        </>
    )
}
